import { ConnectionListResult, FeatureFlagsContext, GridSortProps, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  FlowUpstreamFlowsFilteredByFlowOutputFieldQuery,
  FlowUpstreamFlowsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamFlowsConnection_nodes,
  FlowUpstreamFlowsFilteredByFlowOutputFieldQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamFlowsFilteredByFlowOutputFieldQuery';
import {
  FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery,
  FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_upstreamFlowsConnection_nodes,
  FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery';
import {
  FlowUpstreamFlowsQuery,
  FlowUpstreamFlowsQuery_flows_upstreamFlowsConnection_nodes,
  FlowUpstreamFlowsQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamFlowsQuery';
import {
  FlowUpstreamFlowsWithoutPersonalSpaceQuery,
  FlowUpstreamFlowsWithoutPersonalSpaceQuery_flows_upstreamFlowsConnection_nodes,
  FlowUpstreamFlowsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/FlowUpstreamFlowsWithoutPersonalSpaceQuery';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { FlowUpstreamFlowsFilteredByFlowOutputFieldQueryGql } from '../../queries/FlowUpstreamFlowsFilteredByFlowOutputFieldQueryGql';
import { FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql } from '../../queries/FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql';
import { FlowUpstreamFlowsQueryGql } from '../../queries/FlowUpstreamFlowsQueryGql';
import { FlowUpstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/FlowUpstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = FlowUpstreamFlowsQueryVariables;
type QueryResults = FlowUpstreamFlowsQuery;
type QueryResultListNode = FlowUpstreamFlowsQuery_flows_upstreamFlowsConnection_nodes;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = FlowUpstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = FlowUpstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowUpstreamFlowsWithoutPersonalSpaceQuery_flows_upstreamFlowsConnection_nodes;

type QueryVariablesWithFilter = FlowUpstreamFlowsFilteredByFlowOutputFieldQueryVariables;
type QueryResultsWithFilter = FlowUpstreamFlowsFilteredByFlowOutputFieldQuery;
type QueryResultListNodeWithFilter =
  FlowUpstreamFlowsFilteredByFlowOutputFieldQuery_flows_outputFieldsConnection_nodes_upstreamFlowsConnection_nodes;

type QueryVariablesWithFilterWithoutPersonalSpace = FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery;
type QueryResultListNodeWithFilterWithoutPersonalSpace =
  FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQuery_flows_outputFieldsConnection_nodes_upstreamFlowsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamFlowsConnection : null;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  return flow ? flow.upstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamFlowsConnection : null;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithFilterWithoutPersonalSpace> | null {
  const flow = resultData && resultData.flows && resultData.flows[0];
  const flowOutputFields = flow && flow.outputFieldsConnection && flow.outputFieldsConnection.nodes;
  const field = flowOutputFields && flowOutputFields[0];
  return field ? field.upstreamFlowsConnection : null;
}

export type FlowUpstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly flowVizportalUrlId: string;
  readonly filteredFlowOutputFieldId: string | null;
  readonly rowCount: number | null;
};

export function FlowUpstreamFlowsQueryGrid(props: FlowUpstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredFlowOutputFieldId ? (
      // Query with Selected Field as Filter
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={FlowUpstreamFlowsFilteredByFlowOutputFieldQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      // Query without Selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={FlowUpstreamFlowsQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredFlowOutputFieldId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowUpstreamFlowsFilteredByFlowOutputFieldWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          selectedFlowOutputFieldId: props.filteredFlowOutputFieldId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={FlowUpstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{
          flowVizportalUrlId: props.flowVizportalUrlId,
          orderBy: props.orderBy as FlowSortOrder,
        }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
